import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, from, throwError, of, timer } from "rxjs";
import { map, catchError, switchMap, delay } from "rxjs/operators";
import {
  SettingsService,
  AndroidSubscribe,
} from "../../swagger/api.client.generated";
import { UserService } from "../authentication/user.service";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class WebPushService {
  /**
   * Service for managing Web Push notification subscriptions.
   * Handles permission requests, subscription creation, and server synchronization.
   */

  private readonly VAPID_PUBLIC_KEY = environment.vapidPublicKey;

  // Storage key for subscription status
  private readonly SUBSCRIPTION_STORAGE_KEY = "webpush_subscription_status";

  constructor(
    private settingsService: SettingsService,
    private userService: UserService,
    private http: HttpClient
  ) {}

  /**
   * Checks if the browser supports push notifications.
   * @returns true if push notifications are supported
   */
  isSupported(): boolean {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  /**
   * Gets the current notification permission status.
   * @returns 'default', 'granted', or 'denied'
   */
  getPermissionStatus(): NotificationPermission {
    if (!("Notification" in window)) {
      throw new Error("Notifications are not supported in this browser");
    }
    return Notification.permission;
  }

  /**
   * Requests notification permission from the user.
   * @returns Observable that resolves to the permission status
   */
  requestPermission(): Observable<NotificationPermission> {
    if (!("Notification" in window)) {
      return throwError(
        () => new Error("Notifications are not supported in this browser")
      );
    }

    if (Notification.permission === "granted") {
      return of(Notification.permission);
    }

    if (Notification.permission === "denied") {
      return throwError(
        () =>
          new Error(
            "Notification permission has been denied. Please enable it in your browser settings."
          )
      );
    }

    // Request permission
    return from(Notification.requestPermission()).pipe(
      map((permission) => {
        if (permission === "denied") {
          throw new Error("Notification permission was denied");
        }
        return permission;
      })
    );
  }

  /**
   * Converts a base64 VAPID public key to Uint8Array format required for subscription.
   * @param base64String - The base64 encoded VAPID public key
   * @returns Uint8Array representation of the key
   */
  private urlBase64ToUint8Array(base64String: string): BufferSource {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray as BufferSource;
  }

  /**
   * Gets the service worker registration.
   * Handles both Angular Service Worker (ngsw-worker.js) and custom service worker.
   * @returns Observable that resolves to the service worker registration
   */
  private getServiceWorkerRegistration(): Observable<ServiceWorkerRegistration> {
    return from(navigator.serviceWorker.ready).pipe(
      catchError((error) => {
        console.error("Error getting service worker registration:", error);
        return throwError(
          () =>
            new Error(
              "Service worker is not available. Please ensure the app is served over HTTPS."
            )
        );
      })
    );
  }

  /**
   * Gets the current push subscription.
   * @returns Observable that resolves to the PushSubscription or null if not subscribed
   */
  getSubscription(): Observable<PushSubscription | null> {
    if (!this.isSupported()) {
      return throwError(
        () => new Error("Push notifications are not supported in this browser")
      );
    }

    return this.getServiceWorkerRegistration().pipe(
      switchMap((registration) => {
        return from(registration.pushManager.getSubscription());
      }),
      catchError((error) => {
        console.error("Error getting push subscription:", error);
        return of(null);
      })
    );
  }

  /**
   * Subscribes to push notifications.
   * Requests permission if needed, creates subscription, and sends it to the server.
   * @returns Observable that resolves to true if subscription was successful
   */
  subscribeToPush(): Observable<boolean> {
    // Check if user is logged in
    if (!this.userService.isUserLoggedIn()) {
      return throwError(
        () =>
          new Error("User must be logged in to subscribe to push notifications")
      );
    }

    // Check browser support
    if (!this.isSupported()) {
      return throwError(
        () => new Error("Push notifications are not supported in this browser")
      );
    }

    // Request permission first
    return this.requestPermission().pipe(
      switchMap((permission) => {
        if (permission !== "granted") {
          return throwError(
            () => new Error("Notification permission was not granted")
          );
        }

        // Get service worker registration
        return this.getServiceWorkerRegistration();
      }),
      switchMap((registration) => {
        // Helper function to create a new subscription
        const createNewSubscription = () => {
          const applicationServerKey = this.urlBase64ToUint8Array(
            this.VAPID_PUBLIC_KEY
          );

          return from(
            registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: applicationServerKey,
            })
          );
        };

        // Check if already subscribed
        return from(registration.pushManager.getSubscription()).pipe(
          switchMap((existingSubscription) => {
            if (existingSubscription) {
              // Check if existing subscription is Windows Push - if so, recreate as WebPush
              const existingEndpoint = existingSubscription.endpoint;
              const isExistingWindowsPush = existingEndpoint && existingEndpoint.toLowerCase().includes("notify.windows.com");
              
              if (isExistingWindowsPush) {
                // Existing subscription is Windows Push - unsubscribe and recreate as WebPush
                console.log("Existing Windows Push subscription detected. Recreating as WebPush...");
                return from(existingSubscription.unsubscribe()).pipe(
                  switchMap(() => timer(500)), // Wait for unsubscribe to complete
                  switchMap(() => createNewSubscription()), // Create new subscription
                  switchMap((subscription) => {
                    if (!subscription) {
                      return throwError(
                        () => new Error("Failed to create push subscription after unsubscribing from Windows Push")
                      );
                    }

                    // Check if Edge created a Windows Push (WNS) subscription instead of WebPush
                    const endpoint = subscription.endpoint;
                    const isWindowsPush = endpoint && endpoint.toLowerCase().includes("notify.windows.com");
                    
                    if (isWindowsPush) {
                      // Edge is still creating WNS subscriptions (likely PWA context)
                      // Accept it but log a helpful message
                      console.warn(
                        "Edge continues to create Windows Push subscriptions. " +
                        "This may be due to PWA installation. " +
                        "For WebPush support, try accessing via regular browser URL (not installed PWA)."
                      );
                    } else {
                      console.log("Successfully recreated subscription as WebPush (FCM)");
                    }

                    // Send the subscription to server (WebPush or Windows Push)
                    return this.sendSubscriptionToServer(subscription).pipe(
                      map(() => {
                        localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, "subscribed");
                        console.log(
                          "Successfully subscribed to push notifications",
                          isWindowsPush ? "(Windows Push/WNS - Edge PWA limitation)" : "(WebPush/FCM)"
                        );
                        return true;
                      })
                    );
                  })
                );
              } else {
                // Already subscribed with WebPush, send to server and return
                console.log("Already subscribed to push notifications (WebPush)");
                return this.sendSubscriptionToServer(existingSubscription).pipe(
                  map(() => {
                    localStorage.setItem(
                      this.SUBSCRIPTION_STORAGE_KEY,
                      "subscribed"
                    );
                    return true;
                  })
                );
              }
            }
            
            // No existing subscription - create new one
            return createNewSubscription().pipe(
              switchMap((subscription) => {
                if (!subscription) {
                  return throwError(
                    () => new Error("Failed to create push subscription")
                  );
                }

                // Check if Edge created a Windows Push (WNS) subscription instead of WebPush
                const endpoint = subscription.endpoint;
                const isWindowsPush = endpoint && endpoint.toLowerCase().includes("notify.windows.com");
                
                if (isWindowsPush) {
                  // Edge on Windows sometimes creates WNS subscriptions when installed as PWA
                  // Try to recreate as WebPush with multiple retry attempts
                  console.warn(
                    "Edge created Windows Push (WNS) subscription. Attempting to recreate as WebPush..."
                  );
                  
                  // Enhanced PWA detection - Edge may use WNS even if not explicitly installed
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
                  const isNavigatorStandalone = (window.navigator as any).standalone === true;
                  const isAndroidApp = document.referrer.includes('android-app://');
                  const isPWA = isStandalone || isNavigatorStandalone || isAndroidApp;
                  
                  // Additional Edge-specific detection
                  const isEdge = navigator.userAgent.includes('Edg/');
                  const isWindows = navigator.platform.includes('Win') || navigator.userAgent.includes('Windows');
                  const isEdgeOnWindows = isEdge && isWindows;
                  
                  // Log detailed diagnostic information
                  console.warn(
                    "Edge created Windows Push (WNS) subscription. Diagnostic info:",
                    {
                      isStandalone,
                      isNavigatorStandalone,
                      isAndroidApp,
                      isPWA,
                      isEdge,
                      isWindows,
                      isEdgeOnWindows,
                      userAgent: navigator.userAgent,
                      platform: navigator.platform,
                      referrer: document.referrer,
                      url: window.location.href
                    }
                  );
                  
                  if (isPWA) {
                    console.warn(
                      "App is running as PWA. Edge may be forced to use Windows Push. " +
                      "To get WebPush, uninstall the PWA and access via regular browser URL."
                    );
                  } else if (isEdgeOnWindows) {
                    console.warn(
                      "Edge on Windows is using Windows Push even though not detected as PWA. " +
                      "This may be due to Edge's internal detection of app-like behavior. " +
                      "Try accessing via Chrome browser or ensure you're using regular Edge (not installed app)."
                    );
                  }
                  
                  // Track for analytics
                  console.log("[Analytics] Windows Push detected on Edge", {
                    isPWA,
                    isEdgeOnWindows,
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                    endpoint: subscription.endpoint.substring(0, 50) + "..."
                  });
                  
                  // Try multiple times to get WebPush (up to 3 attempts)
                  const maxRetries = 3;
                  let retryCount = 0;
                  
                  const tryRecreateWebPush = (currentSubscription: PushSubscription): Observable<boolean> => {
                    retryCount++;
                    console.log(`Attempting to recreate as WebPush (attempt ${retryCount}/${maxRetries})...`);
                    
                    return from(currentSubscription.unsubscribe()).pipe(
                      switchMap((unsubscribed) => {
                        if (!unsubscribed) {
                          console.warn("Failed to unsubscribe from Windows Push");
                          if (retryCount >= maxRetries) {
                            // Final fallback: save the Windows Push subscription
                            return this.sendSubscriptionToServer(currentSubscription).pipe(
                              map(() => {
                                localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, "subscribed");
                                console.warn(
                                  "Could not convert Windows Push to WebPush after multiple attempts. " +
                                  "Subscription saved as Windows Push. " +
                                  "To get WebPush, uninstall the PWA and access via regular browser URL."
                                );
                                return true;
                              })
                            );
                          }
                          // Retry
                          return timer(1000).pipe(
                            switchMap(() => tryRecreateWebPush(currentSubscription))
                          );
                        }

                        // Wait longer between attempts
                        return timer(1000).pipe(
                          switchMap(() => createNewSubscription()),
                          switchMap((newSubscription) => {
                            if (!newSubscription) {
                              if (retryCount >= maxRetries) {
                                return throwError(
                                  () => new Error("Failed to create WebPush subscription after multiple attempts")
                                );
                              }
                              // Retry
                              return timer(1000).pipe(
                                switchMap(() => tryRecreateWebPush(currentSubscription))
                              );
                            }

                            // Check if we got WebPush this time
                            const newEndpoint = newSubscription.endpoint;
                            const isStillWindowsPush = newEndpoint && newEndpoint.toLowerCase().includes("notify.windows.com");

                            if (isStillWindowsPush) {
                              if (retryCount >= maxRetries) {
                                // Edge is still creating WNS subscriptions after all retries
                                // This can happen even if not explicitly installed as PWA
                                // Edge on Windows may choose WNS based on various factors
                                console.warn(
                                  "Edge continues to create Windows Push subscriptions after " + maxRetries + " attempts. " +
                                  "Edge on Windows may prefer WNS in certain contexts (PWA, app-like behavior, or browser settings). " +
                                  "For WebPush support, try: 1) Use Chrome browser, 2) Clear Edge site data and re-subscribe, " +
                                  "3) Check edge://apps/ to ensure app is not installed, 4) Access via regular browser URL."
                                );
                                // Accept it but log a helpful message
                                return this.sendSubscriptionToServer(newSubscription).pipe(
                                  map(() => {
                                    localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, "subscribed");
                                    console.warn(
                                      "Saved Windows Push subscription (WebPush conversion failed). " +
                                      "Edge on Windows is using WNS. This subscription will be saved but notifications " +
                                      "won't work until WNS support is implemented. Consider using Chrome for WebPush support."
                                    );
                                    return true;
                                  })
                                );
                              }
                              // Retry again
                              return timer(1000).pipe(
                                switchMap(() => tryRecreateWebPush(newSubscription))
                              );
                            } else {
                              // Success! Got WebPush
                              console.log("Successfully recreated subscription as WebPush (FCM)");
                              return this.sendSubscriptionToServer(newSubscription).pipe(
                                map(() => {
                                  localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, "subscribed");
                                  console.log("Successfully subscribed to push notifications (WebPush/FCM)");
                                  return true;
                                })
                              );
                            }
                          }),
                          catchError((error) => {
                            console.error(`Error recreating subscription as WebPush (attempt ${retryCount}):`, error);
                            if (retryCount >= maxRetries) {
                              // Final fallback: try to save the original Windows Push subscription
                              return this.sendSubscriptionToServer(currentSubscription).pipe(
                                map(() => {
                                  localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, "subscribed");
                                  console.warn("Saved original Windows Push subscription (all recreation attempts failed)");
                                  return true;
                                }),
                                catchError((saveError) => {
                                  console.error("Failed to save Windows Push subscription:", saveError);
                                  return throwError(() => new Error("Failed to save subscription"));
                                })
                              );
                            }
                            // Retry
                            return timer(1000).pipe(
                              switchMap(() => tryRecreateWebPush(currentSubscription))
                            );
                          })
                        );
                      })
                    );
                  };
                  
                  // Start the retry process
                  return tryRecreateWebPush(subscription);
                }

                // WebPush subscription - send to server
                return this.sendSubscriptionToServer(subscription).pipe(
                  map(() => {
                    // Store subscription status
                    localStorage.setItem(
                      this.SUBSCRIPTION_STORAGE_KEY,
                      "subscribed"
                    );
                    console.log("Successfully subscribed to push notifications (WebPush/FCM)");
                    return true;
                  })
                );
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error("Error subscribing to push notifications:", error);
        localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, "error");
        return throwError(() => error);
      })
    );
  }

  /**
   * Sends the push subscription to the backend server.
   * @param subscription - The PushSubscription object
   * @returns Observable that resolves when the subscription is saved
   */
  private sendSubscriptionToServer(
    subscription: PushSubscription
  ): Observable<number> {
    // Extract subscription keys
    const subscriptionObject = subscription.toJSON();
    const keys = subscriptionObject.keys;

    if (!keys || !keys.p256dh || !keys.auth) {
      return throwError(
        () => new Error("Invalid subscription object: missing keys")
      );
    }

    const endpoint = subscriptionObject.endpoint || subscription.endpoint;

    // Note: Windows Push subscriptions are allowed and will be saved.
    // They will be handled appropriately by the backend notification service.
    // Edge on Windows may create Windows Push (WNS) subscriptions when installed as PWA,
    // which is a valid subscription type that we support.

    // Create AndroidSubscribe object for the API
    const androidSubscribe = new AndroidSubscribe({
      endpoint: endpoint,
      auth: keys.auth,
      p256dh: keys.p256dh,
    });

    // Send to backend
    return this.settingsService.webPushSubscribe(androidSubscribe).pipe(
      catchError((error) => {
        console.error("Error sending subscription to server:", error);
        return throwError(
          () => new Error("Failed to save subscription to server")
        );
      })
    );
  }

  /**
   * Unsubscribes from push notifications.
   * Removes the subscription from browser, deactivates in database, and clears local state.
   * @returns Observable that resolves to true if unsubscription was successful
   */
  unsubscribe(): Observable<boolean> {
    if (!this.isSupported()) {
      return throwError(
        () => new Error("Push notifications are not supported in this browser")
      );
    }

    return this.getSubscription().pipe(
      switchMap((subscription) => {
        // First, deactivate subscription in database
        // This ensures that when resubscribing, a new subscription will be created
        // instead of returning the old active one
        return this.deactivateSubscriptionInDatabase().pipe(
          catchError((error) => {
            // Log but don't fail - we'll still try to unsubscribe from browser
            console.warn(
              "Error deactivating subscription in database (will continue with browser unsubscribe):",
              error
            );
            return of(false); // Continue even if database deactivation fails
          }),
          switchMap(() => {
            // Then unsubscribe from browser (if subscription exists)
            if (!subscription) {
              // No browser subscription, just clear local state
              localStorage.removeItem(this.SUBSCRIPTION_STORAGE_KEY);
              console.log("No browser subscription found, cleared local state");
              return of(true);
            }

            // Unsubscribe from browser
            return from(subscription.unsubscribe()).pipe(
              map((success) => {
                // Clear subscription status regardless of browser unsubscribe result
                localStorage.removeItem(this.SUBSCRIPTION_STORAGE_KEY);
                
                if (success) {
                  console.log("Successfully unsubscribed from push notifications");
                } else {
                  console.warn(
                    "Browser unsubscribe returned false, but local state cleared. " +
                    "Subscription may have already been removed or expired."
                  );
                }
                return true;
              }),
              catchError((error) => {
                // Even if browser unsubscribe fails, clear local state
                console.warn(
                  "Error during browser unsubscribe, but clearing local state:",
                  error
                );
                localStorage.removeItem(this.SUBSCRIPTION_STORAGE_KEY);
                return of(true);
              })
            );
          })
        );
      }),
      catchError((error) => {
        // If we can't even get the subscription, still try to deactivate in database
        // and clear local state
        console.warn(
          "Error getting subscription during unsubscribe, attempting database deactivation:",
          error
        );
        return this.deactivateSubscriptionInDatabase().pipe(
          map(() => {
            localStorage.removeItem(this.SUBSCRIPTION_STORAGE_KEY);
            return true;
          }),
          catchError((dbError) => {
            // Even if database deactivation fails, clear local state
            console.warn("Database deactivation also failed, clearing local state:", dbError);
            localStorage.removeItem(this.SUBSCRIPTION_STORAGE_KEY);
            return of(true);
          })
        );
      })
    );
  }

  /**
   * Deactivates the subscription in the database.
   * This ensures that when resubscribing, a new subscription will be created.
   * @returns Observable that resolves when deactivation is complete
   */
  private deactivateSubscriptionInDatabase(): Observable<boolean> {
    // Call the backend API to deactivate subscriptions
    // Use the SettingsService method for WebPush unsubscribe
    return this.settingsService.webPushUnsubscribe().pipe(
      map(() => true),
      catchError((error) => {
        console.error("Error calling WebPushUnsubscribe endpoint:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Checks if the user is currently subscribed to push notifications.
   * @returns Observable that resolves to true if subscribed
   */
  isSubscribed(): Observable<boolean> {
    return this.getSubscription().pipe(
      map((subscription) => subscription !== null),
      catchError(() => of(false))
    );
  }

  /**
   * Gets the subscription status from localStorage (quick check without async).
   * @returns 'subscribed', 'error', or null
   */
  getSubscriptionStatus(): string | null {
    return localStorage.getItem(this.SUBSCRIPTION_STORAGE_KEY);
  }

  /**
   * Checks if push notifications can be enabled (all prerequisites met).
   * @returns Observable that resolves to true if push notifications can be enabled
   */
  canEnablePush(): Observable<boolean> {
    if (!this.isSupported()) {
      return of(false);
    }

    if (!this.userService.isUserLoggedIn()) {
      return of(false);
    }

    const permission = this.getPermissionStatus();
    if (permission === "denied") {
      return of(false);
    }

    return of(true);
  }
}
